import type {AgentSettings,AfterHoursSettings,FaqEntry,AppointmentRules,AppointmentService} from "./types";

const apiBase=(import.meta.env.VITE_API_BASE_URL??"").replace(/\/$/,"");
const apiUrl=(path:string)=>apiBase?apiBase+path:path;
export type AdminSession={token:string};
export type AdminTenant={
 id:string;name:string;status:string;createdAt:string;
 subscription:{plan:string|null;status:string|null;currentPeriodEnd:string|null};
 metrics:{calls7d:number;appointmentsUpcoming:number;messagesNew:number;openComplaints:number;unresolvedDeliveries:number};
 lastActivityAt:string|null;voiceConfigured:boolean;attentionReasons:string[];
};
export type AdminOverview={tenants:AdminTenant[];totals:{tenants:number;activeTenants:number;attention:number;calls7d:number};generatedAt:string};
export type AdminTenantDetail=AdminTenant&{recentAudit:Array<{id:string;action:string;resourceType:string;resourceId:string|null;actorUserId:string|null;createdAt:string}>};
export class AdminApiError extends Error{constructor(readonly status:number){super("admin request failed");}}
export async function adminRequest<T>(session:AdminSession,path:string,init:RequestInit={},fetcher:typeof fetch=fetch):Promise<T>{
 const headers={authorization:`Bearer ${session.token}`,accept:"application/json",
  ...(init.body?{"content-type":"application/json"}:{}),...(init.headers as Record<string,string>|undefined)};
 const response=await fetcher(apiUrl(path),{...init,headers});if(!response.ok)throw new AdminApiError(response.status);
 return response.json() as Promise<T>;
}
export const loadAdminOverview=(session:AdminSession,fetcher:typeof fetch=fetch)=>adminRequest<AdminOverview>(session,"/v1/admin/overview",{},fetcher);
export type AdminInternalTeam={
 schemaVersion:"mil-internal-team-v1";phase:"5E";mode:"synthetic_sandbox";cycleId:string;generatedAt:string;
 director:{name:string;status:"working";policy:string};
 agents:Array<{id:"nora"|"lia"|"alex";name:string;role:string;mission:string;memoryNamespace:string;autonomyCeiling:"A2";
  status:"available"|"working"|"waiting_approval"|"blocked"|"paused";queueDepth:number;
  currentTask:{taskId:string;title:string;status:"working";autonomy:"A1"|"A2";summary:string;outputs:string[];externalActionPerformed:false};
  metrics:Record<string,number>}>;
 tasks:Array<{
  id:string;tenantId:string;parentTaskId:string|null;target:"nora"|"lia"|"alex"|"director";
  origin:"telegram"|"admin"|"system";command:string;autonomy:"A1"|"A2";
  status:"queued"|"working"|"completed"|"blocked";idempotencyKey:string;
  result:Record<string,unknown>;error:string|null;syntheticOnly:true;
  externalActionPerformed:false;phase6Started:false;
  priority:number;attemptCount:number;maxAttempts:number;availableAt:string;
  leaseOwner:string|null;leaseExpiresAt:string|null;heartbeatAt:string|null;
  executionVersion:"mil-internal-exec-v1";
  createdAt:string;startedAt:string|null;completedAt:string|null;updatedAt:string;
 }>;
 externalActionsPerformed:false;phase6Started:false;
};
export const loadAdminTeam=(session:AdminSession)=>adminRequest<AdminInternalTeam>(session,"/v1/admin/team");
export const loadAdminTenant=(session:AdminSession,id:string)=>adminRequest<AdminTenantDetail>(session,`/v1/admin/tenants/${id}`);
export const setAdminStatus=(session:AdminSession,id:string,status:"active"|"suspended")=>
 adminRequest(session,`/v1/admin/tenants/${id}/status`,{method:"POST",body:JSON.stringify({status})});
export const setAdminPlan=(session:AdminSession,id:string,plan:"basic"|"standard"|"complete")=>
 adminRequest(session,`/v1/admin/tenants/${id}/plan`,{method:"POST",body:JSON.stringify({plan})});

export type AdminFinancialTenant={tenantId:string;plan:string;status:string;monthlyPriceCents:number|null;currency:string;costMonthCents:number|null;costComplete:boolean;marginCents:number|null;marginPercent:number|null};
export type AdminFinancialMetrics={
 period:{start:string;end:string};mrrCents:number;pricedActiveTenants:number;unpricedActiveTenants:number;
 knownCostCents:number;costCoverageTenants:number;knownMarginCents:number;knownMarginPercent:number|null;marginCoverageTenants:number;
 churn:{ratePercent:number|null;cancelled:number;openingActive:number;trackingStartedAt:string;completeWindow:boolean};
 tenants:AdminFinancialTenant[];generatedAt:string;
};
export const loadAdminFinancial=(session:AdminSession)=>adminRequest<AdminFinancialMetrics>(session,"/v1/admin/billing/metrics");
export const setBillingProfile=(session:AdminSession,id:string,monthlyPriceCents:number|null,currency="EUR")=>
 adminRequest(session,`/v1/admin/tenants/${id}/billing/profile`,{method:"POST",body:JSON.stringify({monthlyPriceCents,currency})});
export const setSubscriptionStatus=(session:AdminSession,id:string,status:"trialing"|"active"|"past_due"|"cancelled")=>
 adminRequest(session,`/v1/admin/tenants/${id}/billing/subscription-status`,{method:"POST",body:JSON.stringify({status})});
export const setBillingCost=(session:AdminSession,id:string,period:string,totalCostCents:number,complete:boolean)=>
 adminRequest(session,`/v1/admin/tenants/${id}/billing/cost`,{method:"POST",body:JSON.stringify({period,totalCostCents,complete})});


export type SupportSession={
 id:string;tenantId:string;operatorUserId:string;reason:string;mode:"readonly"|"edit";
 createdAt:string;expiresAt:string;elevatedAt:string|null;closedAt:string|null;
};
export type SupportConfiguration={
 session:SupportSession;agent:AgentSettings|null;faq:FaqEntry[];afterHours:AfterHoursSettings|null;
 appointments:{services:AppointmentService[];rules:AppointmentRules|null};
};
export const createSupportSession=(session:AdminSession,tenantId:string,reason:string)=>
 adminRequest<SupportSession>(session,`/v1/admin/tenants/${tenantId}/support-sessions`,
  {method:"POST",body:JSON.stringify({reason})});
export const elevateSupportSession=(session:AdminSession,tenantId:string,supportSessionId:string)=>
 adminRequest<{session:SupportSession;idempotent:boolean}>(session,`/v1/admin/tenants/${tenantId}/support-sessions/${supportSessionId}/elevate`,
  {method:"POST",body:JSON.stringify({confirm:true})});

export const closeSupportSession=(session:AdminSession,tenantId:string,supportSessionId:string)=>
 adminRequest<{session:SupportSession;idempotent:boolean}>(session,`/v1/admin/tenants/${tenantId}/support-sessions/${supportSessionId}/close`,
  {method:"POST"});
export const loadSupportConfiguration=(session:AdminSession,tenantId:string,supportSessionId:string)=>
 adminRequest<SupportConfiguration>(session,`/v1/admin/tenants/${tenantId}/support-sessions/${supportSessionId}/configuration`);
const supportPath=(tenantId:string,supportSessionId:string,suffix:string)=>
 `/v1/admin/tenants/${tenantId}/support-sessions/${supportSessionId}/${suffix}`;
export const saveSupportAgent=(session:AdminSession,tenantId:string,supportSessionId:string,value:AgentSettings)=>
 adminRequest<AgentSettings>(session,supportPath(tenantId,supportSessionId,"agent-settings"),{method:"PUT",body:JSON.stringify(value)});
export const saveSupportFaq=(session:AdminSession,tenantId:string,supportSessionId:string,value:FaqEntry[])=>
 adminRequest<FaqEntry[]>(session,supportPath(tenantId,supportSessionId,"faq"),{method:"PUT",body:JSON.stringify(value)});
export const saveSupportAfterHours=(session:AdminSession,tenantId:string,supportSessionId:string,value:AfterHoursSettings)=>
 adminRequest<AfterHoursSettings>(session,supportPath(tenantId,supportSessionId,"after-hours"),{method:"PUT",body:JSON.stringify(value)});


export type AdminReleaseEvidence={
 schemaVersion:"mil-release-evidence-bundle-v1";agentVersionId:string;qualitySuiteVersion:string;sourceRevision:string;
 evidenceLevel:"none"|"contract_only"|"simulator_verified"|"staging_verified";
 runs:Array<{runId:string;runnerKind:"synthetic_contract"|"simulator"|"staging";status:string;releaseAllowed:boolean;startedAt:string;endedAt:string}>;
 staging:{target:"staging";eligible:boolean;reasons:string[]};
 production:{target:"production";eligible:boolean;reasons:string[]};
};
export type AdminReleaseVersion={
 agentVersionId:string;qualitySuiteVersion:string;sourceRevision:string;sourceRevisions:string[];agentKey:string;promptVersion:string;
 componentDigests:{prompt:string;knowledge:string;toolContract:string;businessConfig:string;runtimeConfiguration:string};
 model:{provider:string;model:string};voice:{provider:string;voice:string};capabilities:string[];policyVersion:string;
 manifestCreatedAt:string;recordedAt:string;evidence:AdminReleaseEvidence;
};
export type AdminReleaseHistory={tenantId:string;latestKnownVersionId:string|null;versions:AdminReleaseVersion[]};
export const loadAdminReleaseHistory=(session:AdminSession,id:string,fetcher:typeof fetch=fetch)=>
 adminRequest<AdminReleaseHistory>(session,`/v1/admin/tenants/${id}/releases`,{},fetcher);

